import { CropCoordinates, CropRect } from '../types';
import { generateCloudinaryCropUrl } from '@/utils/canvasCropping';

/**
 * Check if a URL is a Cloudinary URL that supports crop transformations
 */
export const isCloudinaryUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('cloudinary.com') || url.includes('res.cloudinary.com');
};

/**
 * Check if a URL is a Supabase storage URL
 */
export const isSupabaseUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('.supabase.co/storage/');
};

/**
 * Get the source type of an image URL for error messaging
 */
export const getImageUrlSource = (url: string): 'cloudinary' | 'supabase' | 'unknown' => {
  if (isCloudinaryUrl(url)) return 'cloudinary';
  if (isSupabaseUrl(url)) return 'supabase';
  return 'unknown';
};

/**
 * Validates that an image URL is compatible with the image editor
 * Throws an error if the URL is not a Cloudinary or Supabase URL
 * 
 * @param url The image URL to validate
 * @param context Additional context for error messaging (e.g., 'primary', 'secondary', 'version switch')
 */
export const validateImageUrlForEditor = (url: string, context?: string): void => {
  if (!url) return;
  
  const isValidUrl = isCloudinaryUrl(url) || isSupabaseUrl(url);
  
  if (!isValidUrl) {
    // Check for common problematic URLs
    const isAwsUrl = url.includes('s3.amazonaws.com') || url.includes('amazonaws.com');
    const isScreenshotUrl = url.includes('pricingsaas-screenshots') || url.includes('screenshots');
    
    let errorMessage = `🚨 INVALID IMAGE URL${context ? ` (${context})` : ''}: Non-Cloudinary/Supabase URLs are not supported in the image editor.`;
    
    if (isAwsUrl || isScreenshotUrl) {
      errorMessage += `\n\n❌ DETECTED AWS/Screenshot URL:\n${url}\n\n🔧 This URL cannot be cropped or processed. Please ensure only Cloudinary or Supabase URLs are used.`;
    } else {
      errorMessage += `\n\nReceived URL: ${url}\n\nOnly Cloudinary (cloudinary.com) and Supabase storage URLs are supported.`;
    }
    
    
    throw new Error(errorMessage);
  }
};

/**
 * Normalize crop coordinates to {left, top, width, height} format
 */
export const normalizeCrop = (
  crop: {x: number, y: number, width: number, height: number} | 
        {left: number, top: number, width: number, height: number} | 
        null
): {left: number, top: number, width: number, height: number} | null => {
  if (!crop) return null;
  
  // Check if it has 'x' and 'y' properties (annotation format)
  if ('x' in crop && 'y' in crop) {
    return {
      left: crop.x,
      top: crop.y,
      width: crop.width,
      height: crop.height
    };
  }
  
  // Already in {left, top, width, height} format
  return crop as {left: number, top: number, width: number, height: number};
};

/**
 * Get current crop coordinates from various sources
 */
export const getCurrentCrop = (
  tempCropRef: React.MutableRefObject<CropRect | null>,
  cropCoordinates: CropCoordinates,
  zoomCoordinates: CropCoordinates
): CropRect | null => {
  if (tempCropRef.current) {
    return tempCropRef.current;
  }
  if (cropCoordinates.innerCrop) {
    return cropCoordinates.innerCrop;
  }
  if (zoomCoordinates.innerCrop) {
    return zoomCoordinates.innerCrop;
  }
  return null;
};

/**
 * Get current secondary crop coordinates
 */
export const getCurrentSecondaryCrop = (
  tempSecondaryCropRef: React.MutableRefObject<CropRect | null>,
  secondaryCropCoordinates: CropCoordinates,
  secondaryZoomCoordinates: CropCoordinates
): CropRect | null => {
  if (tempSecondaryCropRef.current) {
    return tempSecondaryCropRef.current;
  }
  if (secondaryCropCoordinates.innerCrop) {
    return secondaryCropCoordinates.innerCrop;
  }
  if (secondaryZoomCoordinates.innerCrop) {
    return secondaryZoomCoordinates.innerCrop;
  }
  return null;
};

/**
 * Get viewport content as Cloudinary URL (what user sees in viewport)
 */
export const getViewportCropUrl = (
  imageUrl: string, 
  zoomCoords: CropCoordinates, 
  mask?: {x: number, y: number, width: number, height: number} | null, 
  imageElement?: HTMLImageElement,
  skipScaling?: boolean // For high resolution Canva export
): string => {
  // Create crop coordinates that represent the viewport view
  let cropCoordsForViewport = { ...zoomCoords };
  
  // If we have a mask, apply it as an innerCrop to the viewport with white background
  if (mask) {
    cropCoordsForViewport.innerCrop = {
      left: mask.x,
      top: mask.y,
      width: mask.width,
      height: mask.height
    };
  }
  
  // Get the actual rendered dimensions from the image element (crucial for CSS scaling!)
  const naturalWidth = imageElement?.naturalWidth;
  const naturalHeight = imageElement?.naturalHeight;
  const renderedWidth = imageElement?.width;
  const renderedHeight = imageElement?.height;
  
  // Use existing working crop function with proper dimensions
  // For masks, we'll use Cloudinary's background parameter to add white background
  const croppedImageUrl = generateCloudinaryCropUrl(
    imageUrl, 
    cropCoordsForViewport,
    naturalWidth,
    naturalHeight,
    renderedWidth,
    renderedHeight,
    mask ? 'white' : undefined, // Add white background for masks
    skipScaling // Skip scaling for Canva when requested
  );
  
  return croppedImageUrl;
};

/**
 * @deprecated Use getViewportCropUrl with skipScaling=true instead
 */
export const getViewportCropUrlForCanva = (
  imageUrl: string, 
  zoomCoords: CropCoordinates, 
  mask?: {x: number, y: number, width: number, height: number} | null, 
  imageElement?: HTMLImageElement
): string => {
  return getViewportCropUrl(imageUrl, zoomCoords, mask, imageElement, true);
};

/**
 * Generate annotated image (placeholder implementation)
 */
export const generateAnnotatedImage = (
  imageUrl: string, 
  annotations: any[], 
  zoomCoords: CropCoordinates,
  aspectRatio?: string,
  annotationStyle?: any,
  imageElement?: any
): Promise<string> => {
  // For now, just return the original image URL
  // This would be implemented to overlay annotations on the image
  return Promise.resolve(imageUrl);
};

// Removed generateCropParamsFromCoordinates - use generateCloudinaryCropUrl from @/utils/canvasCropping instead